import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import { charactersList } from '@/mocks/characters';
import ElementTag from '@/components/base/ElementTag';

interface SearchPanelProps {
  onClose: () => void;
}

export default function SearchPanel({ onClose }: SearchPanelProps) {
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const query = keyword.trim().toLowerCase();

  const articleResults = useMemo(() => {
    if (!query) return [];
    return newsList
      .filter(
        (a) =>
          a.title.toLowerCase().includes(query) ||
          a.tags.some((t) => t.toLowerCase().includes(query)),
      )
      .slice(0, 5);
  }, [query]);

  const characterResults = useMemo(() => {
    if (!query) return [];
    return charactersList
      .filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.element.includes(query) ||
          c.role.toLowerCase().includes(query),
      )
      .slice(0, 4);
  }, [query]);

  const go = (id: string) => {
    navigate(`/detail/${id}`);
    onClose();
  };

  const hasQuery = query.length > 0;
  const total = articleResults.length + characterResults.length;

  return (
    <div className="border-t border-background-200 bg-background-50">
      <div className="w-full px-4 md:px-8 py-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              autoFocus
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索原神资讯、攻略或角色，例如「胡桃」「6.2版本」"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>

          {!hasQuery && (
            <p className="text-xs text-foreground-500">
              提示：以下为前端演示搜索，支持按标题与角色名模糊匹配资讯与攻略内容。
            </p>
          )}

          {hasQuery && total === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <i className="ri-search-eye-line text-3xl text-foreground-300"></i>
              <p className="text-sm text-foreground-600">
                没有找到与「{keyword}」相关的内容，换个关键词试试吧
              </p>
            </div>
          )}

          {hasQuery && total > 0 && (
            <div className="max-h-[60vh] overflow-y-auto">
              <p className="mb-2 text-xs text-foreground-500">
                共匹配到 <span className="font-semibold text-primary-700">{total}</span> 条结果（演示）
              </p>

              {characterResults.length > 0 && (
                <div className="mb-3">
                  <p className="mb-2 text-xs font-medium text-foreground-500">角色</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {characterResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => go(c.guideId)}
                        className="flex items-center gap-3 rounded-md border border-background-200 bg-background-50 p-2 text-left transition-colors hover:border-primary-300"
                      >
                        <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-background-100">
                          <img
                            src={c.avatar}
                            alt={c.name}
                            className="absolute inset-0 h-full w-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-900">{c.name}</p>
                          <div className="mt-1">
                            <ElementTag element={c.element} />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {articleResults.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium text-foreground-500">资讯与攻略</p>
                  <div className="flex flex-col gap-1">
                    {articleResults.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => go(a.id)}
                        className="flex items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-background-100"
                      >
                        <i className="ri-file-text-line text-foreground-400"></i>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-foreground-800">{a.title}</span>
                          <span className="text-xs text-foreground-400">
                            {a.category} · {a.date}
                          </span>
                        </span>
                        <i className="ri-arrow-right-s-line text-foreground-300"></i>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}